/*
 * AppShell — the layout every authenticated screen renders inside.
 *
 * FE-04: "Layout components reserve the copilot panel region; no screen renders full-bleed
 * without an explicit panel-exempt marker."
 *
 * The marker is `panelExempt`, and it takes a *reason* rather than a boolean. A boolean would be
 * set by whoever found the panel inconvenient; a required string makes the exemption something a
 * reviewer can read and disagree with. Today one screen uses it — login, where there is nothing
 * for a copilot to assist with because nobody has authenticated yet.
 *
 * ── The region is reserved; it is no longer *wide* — decided 12 Aug 2026 ───────────────────────
 *
 * D-07 originally chose a persistent side panel and this shell gave it 22rem unconditionally. The
 * cost of that was paid on every screen in the product and the benefit was never delivered:
 * `copilot` has never been passed by anything, because the module has no UI, so every screen has
 * been running 22rem narrower than it needed to be for a component that does not exist. On the
 * sixteen-column tracker that is columns a recruiter was scanning.
 *
 * §13.1 amends D-07 — the rail becomes **the pinned state of a summoned flyout** rather than the
 * default nobody chose. That is also where the industry landed: Microsoft 365 Copilot, Google's
 * Gemini side panel, Salesforce Einstein, GitHub Copilot Chat and Slack AI all ship a dockable
 * panel that is *toggled*, and the products that keep one open by default are the ones where the
 * assistant is the whole product. This is not one of those: the work here is done by the inline
 * field assists and the workbench artifacts, both of which live inside the page.
 *
 * So the column stays in the grid — FE-04's real point is that the shell owns this region and no
 * screen lays it out for itself — and it collapses to zero width until something is put in it.
 * Reserving *structure* is what stops the retrofit into thirty screens; reserving *width* for an
 * unwritten component was the part that only cost.
 *
 * Every string this renders arrives as a prop, including the accessible labels. That is FE-03,
 * and it is also the cleaner line: a design system owns structure, an app owns copy. A default
 * label here would be an English literal that no catalogue could reach.
 */

import type { ReactNode } from 'react';

export interface ShellLabels {
  /** Accessible name for the primary navigation landmark. */
  readonly nav: string;
  /** Accessible name for the copilot region. */
  readonly copilot: string;
  /** The dock control, in each of its two states. Absent where the column does not dock. */
  readonly collapseNav?: string;
  readonly expandNav?: string;
}

export interface AppShellProps {
  brand: ReactNode;
  /**
   * What stands in for the brand when the column is docked.
   *
   * A separate node rather than the same one scaled down, because they are not the same thing at
   * two sizes: a 3.25rem rail cannot hold a wordmark, and a wordmark shrunk to fit stops being the
   * brand and becomes small text. The brand kit draws the same distinction — a lockup where there
   * is room for one, the symbol alone for a favicon, an avatar or a rail.
   *
   * Optional. Absent leaves the docked cell as it was, holding only the dock control.
   */
  brandMark?: ReactNode;
  header: ReactNode;
  nav: ReactNode;
  labels: ShellLabels;
  children: ReactNode;
  /**
   * Rendered into the reserved region, when something is pinned there.
   *
   * Absent is the ordinary case and it is no longer a placeholder: the column collapses, and the
   * region carries `hidden` so it is out of the accessibility tree as well as out of the layout.
   * An empty landmark a screen reader announces is worse than no landmark.
   */
  copilot?: ReactNode;
  /**
   * Pinned to the foot of the navigation column — in practice, who is signed in.
   *
   * A slot rather than an identity component, for the same reason `nav` is a slot: this package
   * has no session and must not acquire one. The shell owns the position and the one rule that
   * matters — the links scroll, this does not — and the app owns what goes in it.
   *
   * Absent renders nothing at all, so the admin console, which has no profile surface, is
   * unchanged rather than carrying an empty strip.
   */
  navFooter?: ReactNode;
  /**
   * The navigation column, docked to a glyph rail — 26 Aug 2026.
   *
   * *"Can the left panel hide to give more space… like we have for any tool, where we can dock it
   * on left. All the screen will get more space."*
   *
   * The shell owns this rather than the app, for the same reason it owns the copilot region: the
   * column is a **grid track**, and a screen that made its own arrangements about the sidebar would
   * be laying out the shell from inside it. Docked narrows the track from 15rem to 3.25rem, and
   * every screen inside `main` gets the difference without knowing anything happened.
   *
   * Undefined means this app does not dock — the admin console, whose three items never cost enough
   * width to be worth taking back — and then no control renders at all.
   */
  navCollapsed?: boolean;
  onToggleNav?: () => void;
}

export function AppShell({
  brand,
  brandMark,
  header,
  nav,
  labels,
  children,
  copilot,
  navFooter,
  navCollapsed,
  onToggleNav,
}: AppShellProps) {
  const docks = onToggleNav !== undefined;
  const docked = docks && navCollapsed === true;

  return (
    // The attribute rather than a class, because it is *state* and not a variant: a pinned copilot
    // is the same shell in a different mode, and a stylesheet reading `[data-copilot]` says that
    // where a second class name would read as two shells. `data-nav` is the same idea for the
    // column beside it.
    <div
      className="tf-shell"
      data-copilot={copilot ? 'pinned' : 'hidden'}
      data-nav={docked ? 'collapsed' : 'open'}
    >
      {/*
        The dock control sits in the brand cell, which is the top of the navigation column and where
        every tool that docks a left panel puts it. The wordmark goes when the column does — 3.25rem
        holds a glyph and nothing else — but the cell stays, so the button does not move between the
        two states. A control that jumps when pressed is one people press twice.
      */}
      <div className="tf-shell__brand">
        {docked ? (
          /*
            The symbol, once the wordmark no longer fits — 15 Sep 2026.

            The cell used to be empty when docked, which left the rail with no brand at all: eleven
            unnamed glyphs and a dock button. The symbol is what the kit puts in exactly this
            position, and it is also what makes the docked rail still look like the product rather
            than like a toolbar.
          */
          <span className="tf-shell__brand-mark">{brandMark}</span>
        ) : (
          <span className="tf-shell__brand-name">{brand}</span>
        )}
        {docks ? (
          <button
            type="button"
            className="tf-shell__dock"
            onClick={onToggleNav}
            aria-expanded={!docked}
            aria-controls="tf-shell-nav"
            aria-label={docked ? labels.expandNav : labels.collapseNav}
            title={docked ? labels.expandNav : labels.collapseNav}
          >
            <span className="tf-shell__dock-glyph" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      <header className="tf-shell__header">{header}</header>
      {/*
        The column is a stack: the links scroll, the footer does not. A long navigation that pushed
        the signed-in person off the bottom would make the one control everybody needs the one you
        have to scroll for.
      */}
      <div className="tf-shell__sidebar" id="tf-shell-nav">
        <nav className="tf-shell__nav" aria-label={labels.nav}>
          {nav}
        </nav>
        {navFooter ? <div className="tf-shell__nav-footer">{navFooter}</div> : null}
      </div>
      <main className="tf-shell__main">{children}</main>
      <aside className="tf-shell__copilot" aria-label={labels.copilot} hidden={!copilot}>
        {copilot}
      </aside>
    </div>
  );
}

export interface FullBleedProps {
  /**
   * Why this screen has no copilot region. Required, and required to be prose: FE-04 permits the
   * exemption but not an unexplained one. It is written to the DOM as a data attribute so the
   * reason is visible in a screenshot or an inspector, not only in source.
   */
  panelExempt: string;
  children: ReactNode;
}

/** The panel-exempt layout. Centres a single card; used by login and by error pages. */
export function FullBleed({ panelExempt, children }: FullBleedProps) {
  return (
    <div className="tf-shell tf-shell--exempt" data-panel-exempt={panelExempt}>
      <main className="tf-centred">
        <div className="tf-centred__inner">{children}</div>
      </main>
    </div>
  );
}

export interface PublicPageProps {
  /** Why this screen has no copilot region. Same requirement `FullBleed` makes, for FE-04. */
  panelExempt: string;
  /**
   * The brand lockup above the card — a mark, a wordmark, or both.
   *
   * **A node the caller supplies, never a string this package composes**, and that is G-03 rather
   * than taste: the product name is data, it lives in the message catalogue, and
   * `gates/brand-lint.mjs` reads the forbidden name from that catalogue and refuses it anywhere in
   * code. A slot called "brand" is the single most tempting place in the product to type it, so
   * this package never sees it — the app renders `t('app.name')` into here.
   *
   * Optional, because an error page has no reason to introduce itself.
   */
  brand?: ReactNode;
  /** One quiet line under the card: a support address, a legal line, a link back. */
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * The frame every page seen *before* signing in wears — 20 Aug 2026.
 *
 * ── Why a shell rather than a screen ────────────────────────────────────────────────────────────
 *
 * Activation is the first page of ours a person meets: the invitation link opens it, and until now
 * everything pre-session was either the provider's hosted screen or a bare centred card. It is also
 * not the last — a client opening a shared submission and a candidate opening a portal link arrive
 * the same way, with no session and no shell — so the frame is built once, here, where both apps
 * can import it. Getting the layer right costs nothing today and a rewrite later.
 *
 * ── What it adds to `FullBleed`, and what it deliberately does not ──────────────────────────────
 *
 * `FullBleed` centres one card and is exactly right for an error page, which should introduce
 * nothing. This wraps it — one centring implementation, not two — and adds the two things a page
 * that greets somebody needs: a brand slot above and a quiet line below.
 *
 * There is no palette of its own. Every colour and space below is a token, so a page rendered
 * before sign-in is the same product as the one after it, in whichever theme the viewer has. The
 * room for *"the design and theme later"* is the brand slot and the stylesheet — not a second set
 * of values that would then have to be kept in step with the first.
 */
export function PublicPage({ panelExempt, brand, footer, children }: PublicPageProps) {
  return (
    <FullBleed panelExempt={panelExempt}>
      <div className="tf-public">
        {brand ? <div className="tf-public__brand">{brand}</div> : null}
        <div className="tf-public__body">{children}</div>
        {footer ? <div className="tf-public__footer">{footer}</div> : null}
      </div>
    </FullBleed>
  );
}

/**
 * The pre-session frame with room to say something — 09 Sep 2026.
 *
 * ── Why a second public frame, rather than widening the first ───────────────────────────────────
 *
 * `PublicPage` centres one card and is right for the pages a person is *sent* to: a spent
 * invitation, a failed callback, an error. Nobody chooses to be there and nothing should compete
 * with the one sentence that matters.
 *
 * Sign-in is the opposite case. It is the first screen of the product anybody sees, it is arrived
 * at deliberately, and the card on it is small — which on a wide display leaves most of the glass
 * empty. This frame gives that space to the product and keeps the control where a returning user's
 * hand already is.
 *
 * ── The aside is decorative, and says so ────────────────────────────────────────────────────────
 *
 * `aria-hidden` on the canvas is not laziness. It carries no words and states no fact a screen
 * reader would want; a person using one gets the brand, the control and nothing they have to skip
 * past. Anything the page genuinely needs to *say* belongs in the panel beside it, where it is
 * read.
 *
 * The single-column fallback below the breakpoint drops the canvas entirely rather than stacking
 * it. On a phone, decoration above the fold is decoration between a person and the reason they
 * came.
 */
export interface SplitPageProps {
  /** Why this screen has no copilot region — FE-04 requires the reason in prose. */
  readonly panelExempt: string;
  /** The wordmark, rendered by the app because G-03 keeps the name out of this package. */
  readonly brand?: ReactNode;
  /** The decorative region. Rendered `aria-hidden`; never put a fact here. */
  readonly aside: ReactNode;
  /** The quiet line under the control. */
  readonly footer?: ReactNode;
  readonly children: ReactNode;
}

export function SplitPage({ panelExempt, brand, aside, footer, children }: SplitPageProps) {
  return (
    /*
     * **Not through `FullBleed`, and that is the point of this component.**
     *
     * It went through it first, and the result was a centred 26rem card with a large empty area
     * beside it. `FullBleed` is full-bleed in the sense of *no copilot panel*, not *edge to edge*:
     * it puts its children inside `.tf-centred__inner`, which is `inline-size: min(26rem, 100%)`.
     * A two-column grid inside a 26rem box cannot be two columns — the panel column’s own minimum
     * took all of it and the stage collapsed to zero width, so the animation had nowhere to draw.
     *
     * So the exempt shell is rendered here and the grid is laid out against the viewport.
     * `data-panel-exempt` is carried over verbatim because FE-04 wants the reason in the DOM.
     */
    <div className="tf-shell tf-shell--exempt" data-panel-exempt={panelExempt}>
      <main className="tf-split">
        <div className="tf-split__canvas" aria-hidden="true">
          {aside}
        </div>
        <div className="tf-split__panel">
          {brand ? <div className="tf-split__brand">{brand}</div> : null}
          <div className="tf-split__control">{children}</div>
          {footer ? <div className="tf-split__foot">{footer}</div> : null}
        </div>
      </main>
    </div>
  );
}
